<template>
  <v-dialog :model-value="modelValue" max-width="900" :persistent="busy" @update:model-value="$emit('update:modelValue', $event)">
    <v-card>
      <v-card-title>Provider documents · {{ operation?.ticket_number || operation?.reservation_code }}</v-card-title>
      <v-card-text>
        <v-alert v-if="error" type="error" class="mb-3">{{ error }}</v-alert>
        <v-progress-linear v-if="busy" indeterminate />
        <template v-if="data">
          <p class="mb-3">Required: <strong>{{ data.requirement }}</strong> · Status: <strong>{{ data.status.state }}</strong>
            <span v-if="data.status.outstanding !== undefined"> · Outstanding: {{ data.status.outstanding }} {{ data.operation.currency }}</span>
          </p>
          <p v-if="data.operation.provider_document_exception_reason" class="mb-3">Reason: {{ data.operation.provider_document_exception_reason }}</p>
          <template v-if="admin">
            <v-select v-model="requirement" :items="requirements" label="Required provider document" :disabled="busy" />
            <v-textarea v-model="reason" label="Reason (required for foreign invoice, receipt or exemption)" rows="2" :disabled="busy" />
            <v-btn :disabled="busy" class="mb-4" @click="saveRequirement">Save requirement</v-btn>
          </template>
          <h3>CFDIs</h3>
          <p v-if="!data.cfdis.length" class="mb-3">No active CFDI linked.</p>
          <v-list v-else>
            <v-list-item v-for="cfdi in data.cfdis" :key="cfdi._id" @click="downloadCfdi(cfdi)">
              <v-list-item-title>{{ cfdi.uuid }}</v-list-item-title>
              <v-list-item-subtitle>{{ cfdi.cfdi?.Estatus || 'Missing CFDI record' }}</v-list-item-subtitle>
            </v-list-item>
          </v-list>
          <h3>Foreign invoices and receipts</h3>
          <p v-if="!data.documents.length" class="mb-3">No supporting documents attached.</p>
          <v-card v-for="document in data.documents" :key="document._id" variant="outlined" class="my-3 pa-3">
            <strong>{{ document.reference }}</strong> · {{ document.issuer }} · {{ document.date }}
            <p>{{ document.kind }} · {{ document.total }} {{ document.currency }} · {{ document.status }}</p>
            <p>Document ID (use to share): {{ document._id }}</p>
            <p v-for="allocation in document.allocations" :key="String(allocation.operation) + String(allocation.linkedAt)">
              {{ allocation.operation }}: {{ allocation.amount }} {{ document.currency }} → {{ allocation.operationAmount }} {{ allocation.currency }}
              {{ allocation.reversedAt ? '(unlinked)' : '' }}
            </p>
            <p v-if="document.reviewReason">Review: {{ document.reviewReason }}</p>
            <v-btn :disabled="busy" variant="text" @click="downloadDocument(document)">Download</v-btn>
            <template v-if="admin">
              <v-btn :disabled="busy" variant="text" @click="prepareMetadata(document)">Edit details</v-btn>
              <v-btn :disabled="busy" variant="text" @click="prepareReview(document, 'verified')">Verify</v-btn>
              <v-btn :disabled="busy" variant="text" @click="prepareReview(document, 'rejected')">Reject</v-btn>
              <v-btn :disabled="busy || !hasActiveAllocation(document)" variant="text" @click="prepareReview(document, 'unlink')">Unlink from this operation</v-btn>
            </template>
          </v-card>
          <template v-if="['foreign_invoice', 'receipt'].includes(data.requirement)">
            <h3 class="mt-4">Attach {{ data.requirement === 'receipt' ? 'receipt' : 'foreign invoice' }}</h3>
            <v-checkbox v-model="useExisting" label="Share a document already uploaded to another operation" :disabled="busy" />
            <v-text-field v-if="useExisting" v-model="documentId" label="Existing document ID" :disabled="busy" />
            <template v-else>
              <v-file-input v-model="file" accept="application/pdf,image/png,image/jpeg" label="PDF, PNG or JPEG (up to 15 MB)" :disabled="busy" />
              <v-text-field v-model="form.reference" label="Invoice / receipt reference" :disabled="busy" />
              <v-text-field v-model="form.issuer" label="Issuer" :disabled="busy" />
              <v-text-field v-model="form.date" type="date" label="Issue date" :disabled="busy" />
              <v-text-field v-model="form.total" type="number" step="0.01" label="Full document total (negative for credit)" :disabled="busy" />
              <v-text-field v-model="form.currency" label="Document currency (e.g. USD, MXN)" :disabled="busy" />
            </template>
            <v-text-field v-model="form.amount" type="number" step="0.01" label="Amount allocated to this operation, in document currency" :disabled="busy" />
            <v-text-field v-model="form.exchangeRate" type="number" step="any" :label="`Exchange rate: ${data.operation.currency} per 1 document currency (1 if same currency)`" :disabled="busy" />
            <p class="mb-3">Uploading or sharing records receipt only. An administrator must verify the document and its allocations. Sharing requires a new review of the document.</p>
            <v-btn color="primary" :disabled="busy" @click="attach">{{ useExisting ? 'Link existing document' : 'Upload and link' }}</v-btn>
          </template>
        </template>
      </v-card-text>
      <v-card-actions><v-btn :disabled="busy" @click="load">Refresh</v-btn><v-spacer /><v-btn :disabled="busy" @click="$emit('update:modelValue', false)">Close</v-btn></v-card-actions>
    </v-card>
  </v-dialog>
  <v-dialog v-model="reviewDialog" max-width="600" :persistent="busy">
    <v-card>
      <v-card-title>{{ reviewAction }} document</v-card-title>
      <v-card-text>
        <p>Verification or rejection applies to the shared document and all its active allocations. Unlinking affects this operation only.</p>
        <v-textarea v-model="reviewReason" label="Review / unlink reason" :disabled="busy" />
        <v-alert v-if="error" type="error">{{ error }}</v-alert>
      </v-card-text>
      <v-card-actions>
        <v-btn :disabled="busy" @click="reviewDialog = false">Cancel</v-btn>
        <v-btn :disabled="busy || !reviewReason.trim()" @click="submitReview">Confirm</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
  <v-dialog v-model="metadataDialog" max-width="650" :persistent="busy">
    <v-card>
      <v-card-title>Correct document details</v-card-title>
      <v-card-text>
        <p>This preserves the file and allocations and requires a new review.</p>
        <v-alert v-if="error" type="error">{{ error }}</v-alert>
        <v-select v-model="metadataForm.kind" :items="['foreign_invoice', 'receipt']" label="Document type" :disabled="busy" />
        <v-text-field v-model="metadataForm.reference" label="Reference" :disabled="busy" />
        <v-text-field v-model="metadataForm.issuer" label="Issuer" :disabled="busy" />
        <v-text-field v-model="metadataForm.date" type="date" label="Issue date" :disabled="busy" />
        <v-text-field v-model="metadataForm.total" type="number" step="0.01" label="Full document total" :disabled="busy" />
        <v-text-field v-model="metadataForm.currency" label="Currency" :disabled="busy" />
        <v-textarea v-model="metadataForm.reason" label="Correction reason" :disabled="busy" />
      </v-card-text>
      <v-card-actions><v-btn :disabled="busy" @click="metadataDialog = false">Cancel</v-btn>
        <v-btn :disabled="busy || !metadataForm.reason.trim()" @click="submitMetadata">Save correction</v-btn></v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import axios from 'axios';
import { getApiErrorMessage } from '../utils/api';

export default {
  name: 'ProviderDocumentsDialog',
  props: { modelValue: Boolean, operation: Object, admin: Boolean },
  emits: ['update:modelValue', 'changed'],
  data() {
    return { data: null, busy: false, error: '', requirement: 'cfdi', reason: '', requirements: ['cfdi', 'foreign_invoice', 'receipt', 'none'], useExisting: false, documentId: '', file: null,
      form: { reference: '', issuer: '', date: '', total: '', currency: '', amount: '', exchangeRate: '' },
      metadataDialog: false, metadataDocument: null, metadataForm: { reason: '' },
      reviewDialog: false, reviewDocument: null, reviewAction: '', reviewReason: '' };
  },
  watch: {
    modelValue(open) { if (open) this.load(); },
  },
  methods: {
    endpoint(suffix = '') { return process.env.VUE_APP_API_BASE_URL + '/operation/documents/' + this.operation._id + suffix; },
    apply(data) {
      this.data = data;
      this.requirement = data.requirement;
      this.reason = data.operation.provider_document_exception_reason || '';
      this.$emit('changed', data.operation);
    },
    async request(action) {
      if (this.busy) return false;
      this.busy = true; this.error = '';
      try { await action(); return true; }
      catch (error) { this.error = getApiErrorMessage(error, error.message || 'Unable to process provider document.'); return false; }
      finally { this.busy = false; }
    },
    async load() {
      this.data = null; this.file = null; this.documentId = ''; this.useExisting = false; this.reviewDialog = false; this.metadataDialog = false;
      this.form = { reference: '', issuer: '', date: '', total: '', currency: this.operation.currency || 'MXN', amount: '', exchangeRate: '' };
      await this.request(async () => { this.apply((await axios.get(this.endpoint())).data); });
    },
    async saveRequirement() {
      await this.request(async () => this.apply((await axios.put(this.endpoint('/requirement'), { requirement: this.requirement, reason: this.reason, version: this.data.operation.__v || 0 })).data));
    },
    async attach() {
      await this.request(async () => {
        let response;
        if (this.useExisting) {
          if (!/^[a-f\d]{24}$/i.test(this.documentId.trim())) throw new Error('Enter a valid document ID.');
          response = await axios.post(this.endpoint('/link/' + this.documentId.trim()), { amount: this.form.amount, exchangeRate: this.form.exchangeRate });
        } else {
          const file = Array.isArray(this.file) ? this.file[0] : this.file;
          if (!file) throw new Error('Select a file first.');
          const payload = new FormData();
          payload.append('file', file);
          payload.append('kind', this.data.requirement);
          for (const [key, value] of Object.entries(this.form)) payload.append(key, value);
          response = await axios.post(this.endpoint('/upload'), payload);
        }
        this.apply(response.data); this.file = null; this.form.amount = '';
      });
    },
    prepareMetadata(document) {
      this.metadataDocument = document;
      this.metadataForm = { ...document, reason: '' };
      this.error = ''; this.metadataDialog = true;
    },
    async submitMetadata() {
      const fields = ['kind', 'reference', 'issuer', 'date', 'total', 'currency', 'reason'];
      const payload = Object.fromEntries(fields.map(key => [key, this.metadataForm[key]]));
      payload.version = this.metadataDocument.__v;
      const success = await this.request(async () => this.apply((await axios.put(this.endpoint('/metadata/' + this.metadataDocument._id), payload)).data));
      if (success) this.metadataDialog = false;
    },
    hasActiveAllocation(document) { return document.allocations.some(a => String(a.operation) === this.operation._id && !a.reversedAt); },
    prepareReview(document, action) { this.reviewDocument = document; this.reviewAction = action; this.reviewReason = ''; this.error = ''; this.reviewDialog = true; },
    async submitReview() {
      const action = this.reviewAction === 'unlink' ? 'unlink' : 'review';
      const success = await this.request(async () => this.apply((await axios.put(this.endpoint('/' + action + '/' + this.reviewDocument._id), {
        version: this.reviewDocument.__v, status: this.reviewAction, reason: this.reviewReason,
      })).data));
      if (success) this.reviewDialog = false;
    },
    async download(url, filename) {
      await this.request(async () => {
        const response = await axios.get(url, { responseType: 'blob' });
        const objectUrl = URL.createObjectURL(response.data);
        const anchor = document.createElement('a'); anchor.href = objectUrl; anchor.download = filename;
        document.body.appendChild(anchor); anchor.click(); anchor.remove();
        setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
      });
    },
    downloadDocument(doc) { return this.download(process.env.VUE_APP_API_BASE_URL + '/operation/documents/files/' + doc._id, doc.originalName || doc.reference); },
    downloadCfdi(doc) { return this.download(process.env.VUE_APP_API_BASE_URL + '/cfdi/received/' + encodeURIComponent(doc.uuid) + '.xml', doc.uuid + '.xml'); },
  },
};
</script>
